'use client';

import { useState, useEffect, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Flame } from 'lucide-react';
import { LocalStorageHistoryStore } from '@/history/localStorage.store';
import type { HistoryEntry } from '@wordle/game-engine';

interface DatePickerModalProps {
  currentDate: Date;
  onSelectDate: (date: Date) => void;
  onClose: () => void;
  /** Namespace de la piste normale (base). */
  normalNamespace?: string;
  /** Namespace de la piste hardcore (`<base>-hc`). */
  hardcoreNamespace: string;
  /** En cas de mot terminé dans les deux pistes, laquelle prime (mode courant). */
  preferHardcore?: boolean;
  strings: {
    title: string;
    prevMonthLabel: string;
    nextMonthLabel: string;
    closeLabel: string;
    months: string[];
    days: string[];
    hardcoreLabel: string;
  };
}

type DayStatus = { status: 'won' | 'lost'; isHardcore: boolean };

function getDateKey(date: Date): string {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d.toISOString().slice(0, 10);
}

function getDaysInMonth(year: number, month: number): number {
  return new Date(year, month + 1, 0).getDate();
}

function getFirstDayOfMonth(year: number, month: number): number {
  return new Date(year, month, 1).getDay();
}

function isSameDay(a: Date, b: Date): boolean {
  return (
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

function isToday(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return isSameDay(date, today);
}

function isFuture(date: Date): boolean {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const checkDate = new Date(date);
  checkDate.setHours(0, 0, 0, 0);
  return checkDate > today;
}

export function DatePickerModal({
  currentDate,
  onSelectDate,
  onClose,
  normalNamespace,
  hardcoreNamespace,
  preferHardcore = false,
  strings,
}: DatePickerModalProps) {
  const [displayMonth, setDisplayMonth] = useState(() => {
    const d = new Date(currentDate);
    d.setDate(1);
    return d;
  });
  const [normalEntries, setNormalEntries] = useState<HistoryEntry[]>([]);
  const [hardcoreEntries, setHardcoreEntries] = useState<HistoryEntry[]>([]);
  const normalStore = useMemo(
    () => new LocalStorageHistoryStore(normalNamespace),
    [normalNamespace]
  );
  const hardcoreStore = useMemo(
    () => new LocalStorageHistoryStore(hardcoreNamespace),
    [hardcoreNamespace]
  );

  const year = displayMonth.getFullYear();
  const month = displayMonth.getMonth();

  // On empêche de naviguer vers un mois entièrement dans le futur : la flèche
  // « mois suivant » est bloquée dès qu'on affiche le mois courant.
  const now = new Date();
  const canGoNextMonth =
    year < now.getFullYear() ||
    (year === now.getFullYear() && month < now.getMonth());

  // Charge l'historique des deux pistes (normale + hardcore), toutes dates.
  useEffect(() => {
    let cancelled = false;
    async function loadHistory() {
      const [normal, hardcore] = await Promise.all([
        normalStore.loadAll(),
        hardcoreStore.loadAll(),
      ]);
      if (cancelled) return;
      setNormalEntries(normal);
      setHardcoreEntries(hardcore);
    }

    loadHistory();
    return () => {
      cancelled = true;
    };
  }, [normalStore, hardcoreStore]);

  // Maps par date pour un accès rapide au statut de chaque piste.
  const normalByDate = useMemo(() => {
    const map = new Map<string, HistoryEntry>();
    normalEntries.forEach(entry => map.set(entry.dateKey, entry));
    return map;
  }, [normalEntries]);

  const hardcoreByDate = useMemo(() => {
    const map = new Map<string, HistoryEntry>();
    hardcoreEntries.forEach(entry => map.set(entry.dateKey, entry));
    return map;
  }, [hardcoreEntries]);

  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);

  const handlePrevMonth = () => {
    setDisplayMonth(d => {
      const newDate = new Date(d);
      newDate.setMonth(d.getMonth() - 1);
      return newDate;
    });
  };

  const handleNextMonth = () => {
    if (!canGoNextMonth) return;
    setDisplayMonth(d => {
      const newDate = new Date(d);
      newDate.setMonth(d.getMonth() + 1);
      return newDate;
    });
  };

  const handleSelectDay = (day: number) => {
    const selectedDate = new Date(year, month, day);
    if (isFuture(selectedDate)) return;
    
    onSelectDate(selectedDate);
    onClose();
  };

  const getDayStatus = (day: number): DayStatus | null => {
    const dayDate = new Date(year, month, day);
    const dateKey = getDateKey(dayDate);

    const normal = normalByDate.get(dateKey);
    const hardcore = hardcoreByDate.get(dateKey);
    const normalDone =
      normal && normal.status !== 'in-progress' ? normal : null;
    const hardcoreDone =
      hardcore && hardcore.status !== 'in-progress' ? hardcore : null;

    if (!normalDone && !hardcoreDone) return null;

    // Le mot du jour étant partagé, une journée n'est normalement terminée que
    // dans une seule piste (l'autre est verrouillée). En cas de données dans les
    // deux (legacy/test), on privilégie la piste du mode courant.
    let chosen: HistoryEntry;
    let isHardcore: boolean;
    if (normalDone && hardcoreDone) {
      chosen = preferHardcore ? hardcoreDone : normalDone;
      isHardcore = preferHardcore;
    } else if (hardcoreDone) {
      chosen = hardcoreDone;
      isHardcore = true;
    } else {
      chosen = normalDone!;
      isHardcore = false;
    }

    if (chosen.status === 'won') return { status: 'won', isHardcore };
    if (chosen.status === 'lost') return { status: 'lost', isHardcore };
    return null;
  };

  const days = [];
  
  // Jours vides au début du mois
  for (let i = 0; i < firstDay; i++) {
    days.push(null);
  }
  
  // Jours du mois
  for (let day = 1; day <= daysInMonth; day++) {
    days.push(day);
  }

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center px-3"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <motion.div
          className="absolute inset-0 bg-black/60"
          onClick={onClose}
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        />
        <motion.div
          className="relative w-full max-w-sm rounded-xl bg-neutral-900 p-5 text-white shadow-xl"
          initial={{ scale: 0.95, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.95, opacity: 0, y: 20 }}
          transition={{ type: 'spring', stiffness: 260, damping: 20 }}
          onClick={e => e.stopPropagation()}
        >
          <div className="mb-4 flex items-center justify-between">
            <h2 className="text-lg font-semibold">{strings.title}</h2>
          </div>

          {/* Navigation du mois */}
          <div className="mb-4 flex items-center justify-between">
            <button
              type="button"
              onClick={handlePrevMonth}
              className="
                h-10 w-10
                rounded-md
                bg-neutral-700
                flex items-center justify-center
                transition
                hover:bg-neutral-600
                active:scale-95
              "
              aria-label={strings.prevMonthLabel}
            >
              <ChevronLeft className="h-6 w-6 text-white" />
            </button>

            <div className="text-base font-semibold">
              {strings.months[month]} {year}
            </div>

            <button
              type="button"
              onClick={handleNextMonth}
              disabled={!canGoNextMonth}
              className={`
                h-10 w-10
                rounded-md
                flex items-center justify-center
                transition
                ${canGoNextMonth
                  ? 'bg-neutral-700 hover:bg-neutral-600 active:scale-95'
                  : 'bg-neutral-700/40 cursor-not-allowed'
                }
              `}
              aria-label={strings.nextMonthLabel}
            >
              <ChevronRight
                className={`h-6 w-6 ${canGoNextMonth ? 'text-white' : 'text-neutral-600'}`}
              />
            </button>
          </div>

          {/* En-têtes des jours */}
          <div className="mb-2 grid grid-cols-7 gap-1">
            {strings.days.map(day => (
              <div
                key={day}
                className="text-center text-xs font-medium text-neutral-400 py-1"
              >
                {day}
              </div>
            ))}
          </div>

          {/* Grille des jours */}
          <AnimatePresence mode="wait">
            <motion.div
              key={`${year}-${month}`}
              className="grid grid-cols-7 gap-1 min-h-[280px]"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.15 }}
            >
              {days.map((day, index) => {
                if (day === null) {
                  return (
                    <div
                      key={`empty-${index}`}
                      className="aspect-square"
                    />
                  );
                }

                const dayDate = new Date(year, month, day);
                const isSelected = isSameDay(dayDate, currentDate);
                const isTodayDate = isToday(dayDate);
                const isFutureDate = isFuture(dayDate);
                const dayStatus = getDayStatus(day);

                // Détermine la couleur de fond selon le statut
                // Priorité : gagné > perdu > aujourd'hui > normal
                let bgClass = 'bg-neutral-800';
                if (isFutureDate) {
                  bgClass = 'bg-neutral-900/50';
                } else if (dayStatus?.status === 'won') {
                  bgClass = 'bg-[var(--color-game-letter-correct)]'; // Même vert que la grille avec transparence
                } else if (dayStatus?.status === 'lost') {
                  bgClass = 'bg-red-700'; // Même rouge que GameOverModal
                } else if (isTodayDate) {
                  bgClass = 'bg-neutral-700';
                }
                // Pas de backgroundColor pour le jour sélectionné

                // Détermine la couleur du texte
                let textClass = 'text-neutral-200';
                if (isFutureDate) {
                  textClass = 'text-neutral-600';
                } else if (dayStatus) {
                  textClass = 'text-white';
                } else if (isTodayDate || isSelected) {
                  textClass = 'text-white';
                }

                // Bordure pour le jour sélectionné (jaune doré) ou pour aujourd'hui si pas sélectionné et pas gagné
                let borderClass = '';
                if (isSelected) {
                  borderClass = 'border-2 border-yellow-400'; // Bordure jaune doré pour le jour sélectionné
                }

                return (
                  <motion.button
                    key={`${year}-${month}-${day}`}
                    type="button"
                    onClick={() => handleSelectDay(day)}
                    disabled={isFutureDate}
                    initial={{ opacity: 0, scale: 0.8 }}
                    animate={{ opacity: 1, scale: 1 }}
                    transition={{
                      duration: 0.15,
                      delay: (index % 7) * 0.02, // Délai progressif par colonne
                    }}
                    className={`
                      relative
                      aspect-square
                      rounded-md
                      text-sm
                      font-medium
                      transition-colors
                      active:scale-95
                      ${bgClass}
                      ${textClass}
                      ${borderClass}
                      ${isFutureDate ? 'cursor-not-allowed' : 'hover:opacity-80'}
                    `}
                  >
                    {dayStatus?.isHardcore && (
                      <Flame
                        aria-label={strings.hardcoreLabel}
                        className="absolute right-0.5 top-0.5 h-3 w-3 fill-amber-300 text-amber-300 drop-shadow-[0_1px_2px_rgba(0,0,0,0.7)]"
                      />
                    )}
                    {day}
                  </motion.button>
                );
              })}
            </motion.div>
          </AnimatePresence>

          <button
            type="button"
            onClick={onClose}
            className="
              mt-5 w-full rounded-md
              bg-neutral-700 py-2
              text-sm font-bold
              hover:bg-neutral-600
              active:scale-95
            "
          >
            {strings.closeLabel}
          </button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}
